import { useCallback, useEffect, useMemo, useState } from "react"
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux"

import { ThunkAction, ThunkDispatch } from "redux-thunk"

import { usePrevious } from "@doktor-se/bones-ui/dist/web-shared/hooks"
import { Category, GroupedCategories } from "@doktor-se/bones-ui/dist/web-shared/types"

import { featureFlags } from "config"
import { groupCategories } from "lib/format"
import { AppAction } from "reducers"
import { RootState } from "reducers/init"

export const useSubcategories = (categories: Category[]): { [categoryId: number]: GroupedCategories[] } => {
  return useMemo(
    () =>
      categories.reduce(
        (acc, category) => {
          const children = categories.filter(c => c.parentId === category.id)
          if (!!children.length) acc[category.id] = groupCategories(children)
          return acc
        },
        {} as { [category: number]: GroupedCategories[] }
      ),
    [categories]
  )
}

export const useStaffById = (staffId?: string | null) => {
  const staff = useAppSelector(state => state.users.staff)
  return useMemo(() => staff.find(person => person.id === staffId), [staff, staffId])
}

export type ReduxDispatch = ThunkDispatch<RootState, any, AppAction>

export const useAppDispatch = (): ReduxDispatch => {
  return useDispatch<ReduxDispatch>()
}

export type Thunk<Result = void> = ThunkAction<Result, RootState, any, AppAction>

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export const useOnlineStatus = () => {
  const [onlineStatus, setOnlineStatus] = useState(navigator.onLine)

  const handleOnline = useCallback(() => {
    setOnlineStatus(true)
  }, [])

  const handleOffline = useCallback(() => {
    setOnlineStatus(false)
  }, [])

  useEffect(() => {
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [handleOffline, handleOnline])

  return useMemo(() => onlineStatus, [onlineStatus])
}

// this is a custom hook that allows to call stuff when user goes online from offline
export const useCallbackWhenOnlineAgain = (callback: Function) => {
  const onlineStatus = useOnlineStatus()
  const prevOnlineStatus = usePrevious(onlineStatus)
  useEffect(() => {
    if (onlineStatus && onlineStatus !== prevOnlineStatus) {
      callback()
    }
  }, [callback, onlineStatus, prevOnlineStatus])
}

const carealot2EnabledClinics = [
  "c842d4b4-c5f9-4baf-89ef-186ee0c33bb1", // Rosenlunds VC
  "ae2b901d-8922-4ae1-8032-6c158bd09a5e", // Vingåker VC
  "d9d18543-e016-4b1e-8513-0da986aaab33", // Norrtälje Hälsocentral
  "25912571-1e09-44da-8281-c02213ce773e", // Adina Hälsan VC
  "a2a4cf4a-2c69-4feb-b4b9-4bf78e0e9f73", // Alnö VC
  "e7bad00c-efdb-4eef-b8bf-38af619a8e36", // Fridhemsplans VC
  "190552c7-ca88-4e7f-bbb8-51e8652555b2", // Gällivare hälsocentral
  //"c8941de3-5aa0-4f1f-bb48-b5721000059d", // Garnisonsgatans VC - The region does not allow Carealot
  //"09f3912e-0662-419c-a3dc-7f7c3984abf4", // Geria City VC - The region does not allow Carealot
  //"55bab76d-335d-4466-9d70-cea2060c7e4b", // Geria VC - The region does not allow Carealot
  "4ab0a0d0-cd41-4915-941d-f2acd5c08afd", // Husläkarna Falkenberg
  "1bdf86d3-0845-4c6b-b73b-c222490b157b", // Katarina Bangata VC
  //"2ec2c246-e969-4eba-8314-f6d0133cca0b", // Mirum VC - The region does not allow Carealot
  "59c338f4-8311-4d09-9903-c509fa0981f4", // Nacka Strand vårdcentral
  "144117f7-f895-42c8-88c7-474c66f072b1", // Njurunda Vårdcentral
  "481352a8-7e1a-408f-9912-2089c21f34ab", // Norra Djurgårdens VC
  //"24af30a0-a3ae-487f-85eb-943112861b37", // Norrtälje vaccinationsmottagning - a vaccination clinic and they are not yet working in carealot
  "3d4b2207-9161-4f55-99d8-b689c1082f61", // Örestadskliniken VC
  "2112b9f8-bfbd-4200-864a-7b9b4516dff3", // Rimbo-Edsbro VC
  "25f1ef7c-fed0-4fb0-bcee-f31047bfe61c", // Sagoskogens VC
  "dd44dd5e-8676-4b20-9d47-3423b52531ef", // Sävedalens vårdcentral och BVC
  "aa63f3b2-52e8-4f70-a84b-480015311644", // Strandängshälsan VC
  "0251d35c-c047-4f3d-9b69-1325581d0749", // Sundsvalls VC
  "751a8d42-ffa7-4000-a7aa-fd4ea56c9bb4" // Väsby Läkargrupp
]

export const isCarealot2Enabled = (clinicId: string) => carealot2EnabledClinics.includes(clinicId)

export const useCarealotV2 = () => {
  const user = useAppSelector(state => state.auth.user)
  return useMemo(() => {
    const isEnabled = featureFlags.has("carealot2")

    const userClinic = user?.data?.clinic

    const isClinicStaff = !!userClinic && carealot2EnabledClinics.includes(userClinic)

    return isEnabled && isClinicStaff
  }, [user])
}

const disabledClinics = [
  "59c338f4-8311-4d09-9903-c509fa0981f4" //Nacka Strand vårdcentral
]

export const useClinicStaff = () => {
  const user = useAppSelector(state => state.auth.user)
  return useMemo(() => {
    const userClinic = user?.data?.clinic
    return !!userClinic && !disabledClinics.includes(userClinic)
  }, [user])
}

export const useNewSummaryView = () => {
  const user = useAppSelector(state => state.auth.user)
  return useMemo(() => {
    const isEnabled = featureFlags.has("errand_details_view")

    const isClinicStaff = !!user?.data?.clinic

    const userClinic = user?.data?.clinic

    return isEnabled && isClinicStaff && carealot2EnabledClinics.includes(userClinic ?? "")
  }, [user])
}
